// Decorative tributaries for the map. The server's terrain remains authoritative: a stream is an extra narrow water
// strip, never a new terrain code. A network belongs to one terrain / cityVersion; discard it when either changes.
// Discovery uses fixed world blocks and complete, bounded lake components, so viewing a different tile never changes
// which lake owns a stream. All coordinates are cells, x east and y north; queries can cross either world seam.
import { cellHash, wrap } from "./geo";

export interface RiverSeg {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  /** HALF width in world cells. */
  width: number;
  /** The whole tributary's stable identity, shared by its curve segments and wrapped copies. */
  id: string;
}

interface Point { x: number; y: number }
interface Mouth { x: number; y: number; dx: number; dy: number; rank: number }
interface River { id: string; segments: RiverSeg[]; x0: number; y0: number; x1: number; y1: number }

const BLOCK = 16;
/** Work per flood fill is bounded; enormous lakes can keep their existing appearance without tributaries. (Painted
 *  world v2, Oct 15 2026: 1024, so medium and large lakes get streams and rivers too.) */
const MAX_LAKE_CELLS = 1024;
/** Painted world v2: tributaries run up to 18 cells (rivers); discovery reaches past that, so a lake whose river reaches
 *  into a requested rectangle is always found (its route is at most RIVER_LENGTH[1] + a little sway from the lake). */
const DISCOVERY_HALO = 22;
/** Streams: half width 0.06–0.10 (full 0.12–0.20), 6–10 cells long. Rivers, the first tributary of about a quarter of
 *  the lakes of 12 cells or more: half width 0.12–0.19 (full 0.24–0.38), 10–18 cells. */
export const STREAM_HALF: readonly [number, number] = [0.06, 0.1];
export const STREAM_LENGTH: readonly [number, number] = [6, 10];
export const RIVER_HALF: readonly [number, number] = [0.12, 0.19];
export const RIVER_LENGTH: readonly [number, number] = [10, 18];
const RIVER_LAKE_MIN = 12;
const RIVER_CHANCE = 0.25;
const CARDINAL: readonly (readonly [number, number])[] = [[1, 0], [0, 1], [-1, 0], [0, -1]];
const DIRECTIONS: readonly (readonly [number, number])[] = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];

/** The same narrow strip used for painting and scenery-foot water checks: solid core, soft edge, zero beyond width. */
export function riverWaterStrength(distance: number, halfWidth: number): number {
  if (halfWidth <= 0 || distance >= halfWidth) return 0;
  const t = Math.max(0, (distance / halfWidth - 0.35) / 0.65);
  return 1 - t * t * (3 - 2 * t);
}

function segmentDistance(x: number, y: number, s: RiverSeg): number {
  const vx = s.bx - s.ax, vy = s.by - s.ay;
  const t = Math.max(0, Math.min(1, ((x - s.ax) * vx + (y - s.ay) * vy) / (vx * vx + vy * vy || 1)));
  return Math.hypot(x - s.ax - vx * t, y - s.ay - vy * t);
}

export class RiverNetwork {
  private terrain = new Map<number, number>();
  private cities = new Map<number, boolean>();
  private scanned = new Set<number>();
  private resolvedLakeCells = new Set<number>();
  private rivers = new Map<string, River>();

  constructor(
    public readonly size: number,
    public readonly seed: number,
    private terrainAt: (x: number, y: number) => number,
    private cityAt: (x: number, y: number) => boolean,
  ) {
    if (!Number.isInteger(size) || size <= 0) throw new Error("RiverNetwork needs a positive integer world size");
  }

  private index(x: number, y: number): number {
    return wrap(Math.floor(y), this.size) * this.size + wrap(Math.floor(x), this.size);
  }

  private code(x: number, y: number): number {
    const k = this.index(x, y);
    let t = this.terrain.get(k);
    if (t === undefined) {
      t = this.terrainAt(k % this.size, Math.floor(k / this.size));
      this.terrain.set(k, t);
    }
    return t;
  }

  private city(x: number, y: number): boolean {
    const k = this.index(x, y);
    let c = this.cities.get(k);
    if (c === undefined) {
      c = this.cityAt(k % this.size, Math.floor(k / this.size));
      this.cities.set(k, c);
    }
    return c;
  }

  private lake(x: number, y: number): boolean {
    return this.code(x, y) === 3 && !this.city(x, y);
  }

  /** A tiny, conservative landing / channel clearance. Cities keep the same broad dry footing as the lake carve-out. */
  private safePoint(x: number, y: number, width: number, inlet?: ReadonlySet<number>): boolean {
    const clearance = width + 0.12;
    for (let gy = Math.floor(y) - 2; gy <= Math.floor(y) + 2; gy++) {
      for (let gx = Math.floor(x) - 2; gx <= Math.floor(x) + 2; gx++) {
        if (this.city(gx, gy)) {
          const dx = (x - (gx + 0.5)) / (1 + width);
          const dy = (y - (gy + 0.38)) / (0.85 + width);
          if (dx * dx + dy * dy < 1) return false;
        }
        const dx = Math.max(gx - x, 0, x - (gx + 1));
        const dy = Math.max(gy - y, 0, y - (gy + 1));
        if (dx * dx + dy * dy >= clearance * clearance) continue;
        const t = this.code(gx, gy);
        if (t === 2 || t === 4 || (t === 3 && !inlet?.has(this.index(gx, gy)))) return false;
      }
    }
    return true;
  }

  private safeSpan(a: Point, b: Point, width: number, inlet?: ReadonlySet<number>): boolean {
    const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 0.1));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      if (!this.safePoint(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, width, inlet)) return false;
    }
    return true;
  }

  private hash(x: number, y: number, salt: number): number {
    return cellHash(wrap(x, this.size), wrap(y, this.size), this.seed ^ salt);
  }

  /** Flood fill a small lake completely. If it exceeds the cap, mark the visited part and omit its streams. */
  private resolveLake(x: number, y: number): void {
    const first = this.index(x, y);
    if (this.resolvedLakeCells.has(first)) return;
    const seen = new Set<number>([first]);
    const queue = [first];
    let complete = true;
    for (let i = 0; i < queue.length; i++) {
      const k = queue[i], cx = k % this.size, cy = Math.floor(k / this.size);
      for (const [dx, dy] of CARDINAL) {
        const nk = this.index(cx + dx, cy + dy);
        if (seen.has(nk) || !this.lake(cx + dx, cy + dy)) continue;
        seen.add(nk);
        queue.push(nk);
        if (seen.size > MAX_LAKE_CELLS) { complete = false; break; }
      }
      if (!complete) break;
    }
    for (const k of seen) this.resolvedLakeCells.add(k);
    if (!complete || seen.size < 4) return;
    const cells = [...seen].sort((a, b) => a - b);
    this.buildLake(cells, seen);
  }

  private buildLake(cells: number[], inlet: ReadonlySet<number>): void {
    const owner = cells[0];
    const ownerX = owner % this.size, ownerY = Math.floor(owner / this.size);
    const mouths: Mouth[] = [];
    for (const k of cells) {
      const x = k % this.size, y = Math.floor(k / this.size);
      for (let d = 0; d < CARDINAL.length; d++) {
        const [dx, dy] = CARDINAL[d];
        const t = this.code(x + dx, y + dy);
        if (t !== 0 && t !== 1) continue;
        mouths.push({ x, y, dx, dy, rank: this.hash(x, y, owner ^ (711 + d * 37)) });
      }
    }
    mouths.sort((a, b) => a.rank - b.rank || a.y - b.y || a.x - b.x || a.dy - b.dy || a.dx - b.dx);
    const h = this.hash(ownerX, ownerY, 727);
    const wanted = cells.length >= 30 ? (h < 0.5 ? 3 : 2) : h < 0.45 ? 2 : 1;
    // about a quarter of the medium and large lakes are fed by a broader river (their first tributary)
    let river0 = cells.length >= RIVER_LAKE_MIN && this.hash(ownerX, ownerY, 733) < RIVER_CHANCE;
    const selected: River[] = [];
    // Limit failed routing work as well as discovery; crowded lakes simply get fewer decorative tributaries.
    for (const mouth of mouths.slice(0, 28)) {
      const river = this.route(mouth, owner, inlet, river0) ?? (river0 ? this.route(mouth, owner, inlet, false) : null);
      if (!river) continue;
      if (selected.some(r => river.segments.some(s => r.segments.some(o => segmentDistance(s.ax, s.ay, o) < 0.9 + s.width + o.width)))) continue;
      river0 = false;
      selected.push(river);
      this.rivers.set(river.id, river);
      if (selected.length >= wanted) break;
    }
  }

  private route(mouth: Mouth, owner: number, inlet: ReadonlySet<number>, broad = false): River | null {
    const { x, y, dx, dy } = mouth;
    const [w0, w1] = broad ? RIVER_HALF : STREAM_HALF;
    const [l0, l1] = broad ? RIVER_LENGTH : STREAM_LENGTH;
    const width = w0 + this.hash(x, y, 743 + dx * 11 + dy) * (w1 - w0);
    const points: Point[] = [{ x: x + 0.5, y: y + 0.5 }, { x: x + dx + 0.5, y: y + dy + 0.5 }];
    if (!this.safeSpan(points[0], points[1], width, inlet) || !this.safePoint(points[1].x, points[1].y, width)) return null;
    const target = l0 + this.hash(x, y, 751 + dx * 13 + dy) * (l1 - l0);
    const phase = this.hash(x, y, 757) * Math.PI * 2;
    const heading = Math.atan2(dy, dx);
    const used = new Set<number>(points.map(p => this.index(p.x, p.y)));
    let length = 1;
    for (let step = 1; length < target && step < Math.ceil(l1) + 4; step++) {
      const a = points[points.length - 1], prev = points[points.length - 2];
      const vx = a.x - prev.x, vy = a.y - prev.y, vl = Math.hypot(vx, vy);
      const desired = heading + Math.sin(phase + step * 0.75) * 0.7;
      const choices: { p: Point; score: number }[] = [];
      for (let d = 0; d < DIRECTIONS.length; d++) {
        const [qx, qy] = DIRECTIONS[d], ql = Math.hypot(qx, qy);
        if ((qx * vx + qy * vy) / (ql * vl) < 0.6) continue;
        const p = { x: a.x + qx, y: a.y + qy };
        if (used.has(this.index(p.x, p.y)) || !this.safeSpan(a, p, width)) continue;
        const score = (Math.cos(desired) * qx + Math.sin(desired) * qy) / ql + this.hash(Math.floor(p.x), Math.floor(p.y), 769 + step * 19 + d) * 0.65;
        choices.push({ p, score });
      }
      choices.sort((a, b) => b.score - a.score);
      if (!choices.length) return null;
      const next = choices[0].p;
      length += Math.hypot(next.x - a.x, next.y - a.y);
      points.push(next);
      used.add(this.index(next.x, next.y));
    }
    if (length < l0 - 0.5) return null;
    const id = `${broad ? "broad" : "river"}:${this.seed}:${owner}:${this.index(x, y)}:${dx},${dy}`;
    const sway = 0.15 + this.hash(x, y, 787) * 0.07;
    let segments: RiverSeg[] | null = null;
    // A grid route can run straight for several cells. Gently sway its interior points sideways before interpolation,
    // keeping the inlet and source fixed. A crowded bank gets half sway or the original curve, with the same safety
    // checks at every strength; discovery and bridge placement remain independent of the requesting tile or zoom.
    for (const strength of [1, 0.5, 0]) {
      const shaped = points.map((p, i) => {
        if (i === 0 || i === points.length - 1 || strength === 0) return p;
        const a = points[i - 1], b = points[i + 1];
        const vx = b.x - a.x, vy = b.y - a.y, len = Math.hypot(vx, vy) || 1;
        const offset = sway * strength * Math.sin(phase + i * 0.95);
        return { x: p.x - vy / len * offset, y: p.y + vx / len * offset };
      });
      segments = this.curvedSegments(shaped, width, id, inlet);
      if (segments) break;
    }
    if (!segments) return null;
    return {
      id, segments,
      x0: Math.min(...segments.map(s => Math.min(s.ax, s.bx) - s.width)),
      y0: Math.min(...segments.map(s => Math.min(s.ay, s.by) - s.width)),
      x1: Math.max(...segments.map(s => Math.max(s.ax, s.bx) + s.width)),
      y1: Math.max(...segments.map(s => Math.max(s.ay, s.by) + s.width)),
    };
  }

  private curvedSegments(points: Point[], width: number, id: string, inlet: ReadonlySet<number>): RiverSeg[] | null {
    const segments: RiverSeg[] = [];
    // Validate every resulting Hermite span: both a corner and its sway can touch land that the grid route avoided.
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i], b = points[i + 1], before = points[Math.max(0, i - 1)], after = points[Math.min(points.length - 1, i + 2)];
      const mx0 = (b.x - before.x) * 0.45, my0 = (b.y - before.y) * 0.45;
      const mx1 = (after.x - a.x) * 0.45, my1 = (after.y - a.y) * 0.45;
      let prev = a;
      for (let j = 1; j <= 6; j++) {
        const t = j / 6, t2 = t * t, t3 = t2 * t;
        const p = {
          x: (2 * t3 - 3 * t2 + 1) * a.x + (t3 - 2 * t2 + t) * mx0 + (-2 * t3 + 3 * t2) * b.x + (t3 - t2) * mx1,
          y: (2 * t3 - 3 * t2 + 1) * a.y + (t3 - 2 * t2 + t) * my0 + (-2 * t3 + 3 * t2) * b.y + (t3 - t2) * my1,
        };
        if (!this.safeSpan(prev, p, width, i === 0 ? inlet : undefined)) return null;
        // narrowing upstream, and to a thin spring over its last cell
        const along = (i + t) / (points.length - 1);
        const w = Math.max(0.022, width * (1 - 0.3 * along) * (i === points.length - 2 ? 1 - 0.62 * t * t : 1));
        segments.push({ ax: prev.x, ay: prev.y, bx: p.x, by: p.y, width: w, id });
        prev = p;
      }
    }
    return segments;
  }

  private axisBlocks(lo: number, hi: number): number[] {
    const n = Math.ceil(this.size / BLOCK);
    if (hi - lo >= this.size) return Array.from({ length: n }, (_, i) => i);
    const out = new Set<number>();
    for (let q = Math.floor(lo); q <= Math.ceil(hi); q++) out.add(Math.floor(wrap(q, this.size) / BLOCK));
    return [...out].sort((a, b) => a - b);
  }

  private discover(x0: number, y0: number, x1: number, y1: number): void {
    const n = Math.ceil(this.size / BLOCK);
    for (const by of this.axisBlocks(y0 - DISCOVERY_HALO, y1 + DISCOVERY_HALO)) {
      for (const bx of this.axisBlocks(x0 - DISCOVERY_HALO, x1 + DISCOVERY_HALO)) {
        const k = by * n + bx;
        if (this.scanned.has(k)) continue;
        this.scanned.add(k);
        for (let y = by * BLOCK; y < Math.min(this.size, (by + 1) * BLOCK); y++) {
          for (let x = bx * BLOCK; x < Math.min(this.size, (bx + 1) * BLOCK); x++) {
            if (this.lake(x, y)) this.resolveLake(x, y);
          }
        }
      }
    }
  }

  /** Curve spans touching the requested rectangle, translated into its unwrapped world coordinates. */
  segments(x0: number, y0: number, x1: number, y1: number): RiverSeg[] {
    if (![x0, y0, x1, y1].every(Number.isFinite) || x1 < x0 || y1 < y0) return [];
    this.discover(x0, y0, x1, y1);
    const out: RiverSeg[] = [];
    for (const r of this.rivers.values()) {
      const kx0 = Math.ceil((x0 - r.x1) / this.size), kx1 = Math.floor((x1 - r.x0) / this.size);
      const ky0 = Math.ceil((y0 - r.y1) / this.size), ky1 = Math.floor((y1 - r.y0) / this.size);
      for (let ky = ky0; ky <= ky1; ky++) for (let kx = kx0; kx <= kx1; kx++) {
        const dx = kx * this.size, dy = ky * this.size;
        for (const s of r.segments) {
          const ax = s.ax + dx, ay = s.ay + dy, bx = s.bx + dx, by = s.by + dy;
          if (Math.max(ax, bx) + s.width < x0 || Math.min(ax, bx) - s.width > x1 || Math.max(ay, by) + s.width < y0 || Math.min(ay, by) - s.width > y1) continue;
          out.push({ ax, ay, bx, by, width: s.width, id: s.id });
        }
      }
    }
    return out.sort((a, b) => a.id.localeCompare(b.id) || a.ax - b.ax || a.ay - b.ay || a.bx - b.bx || a.by - b.by);
  }

  /** Water strength at a world point, using exactly the same curve spans and widths as rendering. */
  waterAt(x: number, y: number): number {
    let strength = 0;
    for (const s of this.segments(x, y, x, y)) strength = Math.max(strength, riverWaterStrength(segmentDistance(x, y, s), s.width));
    return strength;
  }
}
